/**
 * Console suppression utility
 * 
 * This utility replaces the standard console methods with no-op functions
 * to suppress non-error logs in production environments.
 * 
 * Usage:
 * - Import and call suppressConsoleOutput() early in your application
 * - Only error and warn logs will be preserved
 * - All other console methods will be disabled
 */

/**
 * Suppress non-error console output
 * @param preserveWarnings Whether to preserve console.warn messages (default: true)
 */
export function suppressConsoleOutput(preserveWarnings = true): void {
  // Store original methods
  const originalConsole = {
    log: console.log,
    info: console.info,
    debug: console.debug,
    warn: console.warn,
    error: console.error,
    trace: console.trace,
  };

  // Replace non-error methods with no-op function
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.trace = () => {};
  
  // Optionally preserve warnings
  if (!preserveWarnings) {
    console.warn = () => {};
  }
  
  // Always preserve errors
  // console.error remains unchanged

  // Add method to restore console functionality if needed
  (console as any).restoreOutput = () => {
    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.debug = originalConsole.debug;
    console.warn = originalConsole.warn;
    console.trace = originalConsole.trace;
    // console.error is always preserved
  };
}

/**
 * Restore original console output methods
 */
export function restoreConsoleOutput(): void {
  if ((console as any).restoreOutput) {
    (console as any).restoreOutput();
  }
}
